import { useState, useMemo } from 'react';
import { ShieldCheck, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

const COMMON = new Set([
  'password', '123456', 'qwerty', 'admin', 'letmein', 'iloveyou', 'abc123', 'monkey',
  'dragon', 'sunshine', 'football', 'baseball', 'shadow', 'master', '111111', '000000',
]);

function entropyBits(pw: string): number {
  let pool = 0;
  if (/[a-z]/.test(pw)) pool += 26;
  if (/[A-Z]/.test(pw)) pool += 26;
  if (/\d/.test(pw)) pool += 10;
  if (/[^A-Za-z0-9]/.test(pw)) pool += 32;
  if (pool === 0) return 0;
  return pw.length * Math.log2(pool);
}

function timeToCrack(bits: number): string {
  // Assume 10 billion guesses/sec
  const seconds = Math.pow(2, bits) / 1e10;
  if (seconds < 1) return 'instantly';
  if (seconds < 60) return `${seconds.toFixed(0)} seconds`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(0)} minutes`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(0)} hours`;
  if (seconds < 86400 * 365) return `${(seconds / 86400).toFixed(0)} days`;
  if (seconds < 86400 * 365 * 1000) return `${(seconds / (86400 * 365)).toFixed(1)} years`;
  if (seconds < 86400 * 365 * 1e9) return `${(seconds / (86400 * 365 * 1000)).toFixed(0)} thousand years`;
  return 'longer than the age of the universe';
}

interface Check {
  label: string;
  pass: boolean;
}

export function PasswordStrength() {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);

  const analysis = useMemo(() => {
    if (!pw) return null;
    const bits = entropyBits(pw);
    const checks: Check[] = [
      { label: 'At least 12 characters', pass: pw.length >= 12 },
      { label: 'Contains lowercase letters', pass: /[a-z]/.test(pw) },
      { label: 'Contains uppercase letters', pass: /[A-Z]/.test(pw) },
      { label: 'Contains numbers', pass: /\d/.test(pw) },
      { label: 'Contains symbols', pass: /[^A-Za-z0-9]/.test(pw) },
      { label: 'Not in common-passwords list', pass: !COMMON.has(pw.toLowerCase()) },
      { label: 'No repeated triplets (aaa)', pass: !/(.)\1{2,}/.test(pw) },
      { label: 'No sequential runs (abc, 123)', pass: !/(?:abc|qwe|asd|zxc|123|234|345|456|789)/i.test(pw) },
    ];
    const passed = checks.filter((c) => c.pass).length;
    const score = Math.min(100, (bits / 80) * 100);
    return { bits, checks, passed, score };
  }, [pw]);

  const status = analysis
    ? analysis.bits >= 80
      ? { label: 'Excellent', color: 'text-success', icon: ShieldCheck }
      : analysis.bits >= 60
      ? { label: 'Strong', color: 'text-success', icon: ShieldCheck }
      : analysis.bits >= 40
      ? { label: 'Moderate', color: 'text-warning', icon: ShieldAlert }
      : { label: 'Weak', color: 'text-danger', icon: ShieldAlert }
    : null;

  return (
    <div>
      <ToolHeader
        toolId="password-strength"
        title="Password strength"
        description="Estimate password entropy, detect common patterns, see crack-time projection."
      />

      <Card className="mb-4">
        <Field label="Password">
          <div className="relative">
            <Input
              type={show ? 'text' : 'password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="font-mono pr-10"
              placeholder="Enter a password to analyze"
            />
            <button
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-subtle hover:text-fg"
            >
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </Field>
      </Card>

      {analysis && status && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div className={cn('flex items-center gap-2 text-lg font-semibold', status.color)}>
                <status.icon size={18} />
                {status.label}
              </div>
              <div className="text-sm text-fg-muted">
                ~<span className="font-mono">{analysis.bits.toFixed(0)}</span> bits of entropy
              </div>
            </div>
            <div className="h-2 rounded-full bg-bg-inset overflow-hidden mb-4">
              <div
                className={cn(
                  'h-full transition-all',
                  analysis.bits >= 60 ? 'bg-success' : analysis.bits >= 40 ? 'bg-warning' : 'bg-danger',
                )}
                style={{ width: `${analysis.score}%` }}
              />
            </div>
            <div className="text-xs text-fg-muted">
              Online attacks (10B guesses/sec) would take{' '}
              <span className="font-mono font-medium text-fg">{timeToCrack(analysis.bits)}</span>
            </div>
          </Card>
          <Card>
            <div className="text-xs font-medium text-fg-muted mb-3">
              {analysis.passed} of {analysis.checks.length} checks passed
            </div>
            <div className="space-y-1.5">
              {analysis.checks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div
                    className={cn(
                      'size-3.5 rounded-full flex items-center justify-center text-2xs font-bold',
                      c.pass ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger',
                    )}
                  >
                    {c.pass ? '✓' : '×'}
                  </div>
                  <span className="text-fg-muted">{c.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PasswordStrength;
